import React from 'react';
import {
  FastField,
  Field,
  useField,
  type FieldProps,
  type FieldValidator,
} from 'formik';
import KoobInputStepper from '../KoobInputStepper';
import KoobInputLabel from '../KoobInputLabel';

type KoobInputStepperFieldProps = {
  name: string;
  /** Read the error from another path, e.g. an array-level range error */
  errorKey?: string;
  label?: React.ReactNode;
  required?: boolean;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Show the error before the field is touched when false */
  touchRequired?: boolean;
  /** Use formik FastField (skips re-renders of untouched fields) */
  fast?: boolean;
  /** Formik field-level validation, e.g. a bound that depends on sibling data */
  validate?: FieldValidator;
  'data-testid'?: string;
};

export const KoobInputStepperField = ({
  name,
  errorKey,
  label,
  required,
  min,
  max,
  disabled,
  size,
  touchRequired = true,
  fast = false,
  validate,
  'data-testid': dataCy,
}: KoobInputStepperFieldProps) => {
  const FieldComponent = fast ? FastField : Field;
  const [, errorKeyMeta] = useField(errorKey ?? name);

  return (
    <FieldComponent name={name} validate={validate}>
      {({ field, meta: ownMeta, form }: FieldProps) => {
        const meta = errorKey ? errorKeyMeta : ownMeta;
        const hasError = touchRequired
          ? Boolean(meta.error && meta.touched)
          : Boolean(meta.error);

        const stepper = (
          <KoobInputStepper
            name={name}
            value={field.value}
            onValueChange={(value) => form.setFieldValue(name, value)}
            onBlur={field.onBlur}
            min={min}
            max={max}
            disabled={disabled}
            size={size}
            error={hasError}
            data-testid={dataCy ?? `stepper-${name}`}
          />
        );

        return (
          <div className="flex flex-col gap-1">
            {label != null ? (
              <KoobInputLabel
                label={label}
                required={required}
                disabled={disabled}
              >
                {stepper}
              </KoobInputLabel>
            ) : (
              stepper
            )}
            {hasError && (
              <p className="m-0 text-sm text-red-500">{meta.error}</p>
            )}
          </div>
        );
      }}
    </FieldComponent>
  );
};

export default KoobInputStepperField;
